import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { firebaseConnect, getFirebase, isLoaded } from 'react-redux-firebase';
import moment from 'moment';

import './CompanyForm.css';
import logoUrl from 'assets/img/logo-black.png';

import { toast } from 'react-toastify';
import {
  Company,
  Employee,
  Block,
  Cell,
  VideoCell,
  PhotoCell,
  PhotoCarouselCell,
  ParagraphCell,
  AddressCell,
  BlogPostCell
} from '../../../utils/variables';
import LoadingSpinner from 'components/global/LoadingSpinner/LoadingSpinner';
import CompanyFormNav from '../CompanyFormNav/CompanyFormNav';
import FormField from '../../contentform/FormField/FormField';
import ContentBlockEditor from '../../contentform/ContentBlockEditor/ContentBlockEditor';

import truncateString from 'utils/truncateString';

class CompanyForm extends React.Component {
  static propTypes = {
    companyKey: PropTypes.string,
    companies: PropTypes.object,
    location: PropTypes.object,
    blogPosts: PropTypes.array
  };

  state = {
    industryIndex: null,
    companyInfo: null,
    companyProfile: null,
    companyOffice: null,
    companyPerson: null,
    _scrolledSection: null
  };

  defaultProfileBlocks = [
    new Block('showcase', 'B', this.getBlockContent('showcase', 'B')),
    new Block('showcase', 'A', this.getBlockContent('showcase', 'A')),
    new Block('content', 'A', this.getBlockContent('content', 'A')),
    new Block('showcase', 'B', this.getBlockContent('showcase', 'B')),
    new Block('content', 'B', this.getBlockContent('content', 'B'))
  ];

  defaultOfficeBlocks = [
    new Block('showcase', 'A', this.getBlockContent('showcase', 'A')),
    new Block('content', 'B', this.getBlockContent('content', 'B')),
    new Block('content', 'A', this.getBlockContent('content', 'A'))
  ];

  databaseToast = null;

  // First, retrieve the company key from the URL
  // Load company and all related parts once from Firebase
  componentDidMount() {
    const { companyKey } = this.props;
    const firebase = getFirebase();

    if (companyKey && companyKey !== 'new') {
      const tagCompanyIndustryIndexRef = firebase.database()
        .ref(`/tagCompanyIndustryIndex`);
      const companyInfoRef = firebase.database()
        .ref(`/company/${companyKey}`);
      const companyProfileRef = firebase.database()
        .ref(`/companyProfile/${companyKey}`);
      const companyOfficeRef = firebase.database()
        .ref(`/companyOffice/${companyKey}`);
      const companyPersonRef = firebase.database()
        .ref(`/companyPerson/${companyKey}`);

      const retrievePromises = [
        tagCompanyIndustryIndexRef.once('value'),
        companyInfoRef.once('value'),
        companyProfileRef.once('value'),
        companyOfficeRef.once('value'),
        companyPersonRef.once('value')
      ];

      Promise.all(retrievePromises)
        .then((results) => {
          const data = results.map(result => result.val());

          const tagCompanyIndustryIndex = data[0] ? Object.keys(data[0])
            .map(key => ({ value: key, label: key })) : [];
          const companyInfoMerged = data[1] ? Object.assign(new Company(), data[1]) : new Company();
          const companyProfileMerged = data[2] ? data[2].map(block => (Object.assign(new Block(block.type, block.layout, this.getBlockContent(block.type, block.layout)), block))) : this.defaultProfileBlocks.slice();
          const companyOfficeMerged = data[3] ? data[3].map(block => (Object.assign(new Block(block.type, block.layout, this.getBlockContent(block.type, block.layout)), block))) : this.defaultOfficeBlocks.slice();
          const companyPersonMerged = data[4] ? data[4] : {};

          // Merge nested content in blocks
          companyProfileMerged.forEach(block => {
            if (!block.content) {
              block.content = this.getBlockContent(block.type, block.layout);
            } else {
              const defaultContent = this.getBlockContent(block.type, block.layout);
              const existingContent = block.content.map(cell => (Object.assign(this.generateCell(cell.type), cell)));
              block.content = Object.assign(defaultContent, existingContent);
            }
          });
          companyOfficeMerged.forEach(block => {
            if (!block.content) {
              block.content = this.getBlockContent(block.type, block.layout);
            } else {
              const defaultContent = this.getBlockContent(block.type, block.layout);
              const existingContent = block.content.map(cell => (Object.assign(this.generateCell(cell.type), cell)));
              block.content = Object.assign(defaultContent, existingContent);
            }
          });
          // Remember that companyPerson is an object containing multiple block sets
          Object.keys(companyPersonMerged)
            .forEach(personKey => {
              let person = companyPersonMerged[personKey];
              person.forEach(block => {
                if (!block.content) {
                  block.content = this.getBlockContent(block.type, block.layout);
                } else {
                  const defaultContent = this.getBlockContent(block.type, block.layout);
                  const existingContent = block.content.map(cell => (Object.assign(this.generateCell(cell.type), cell)));
                  block.content = Object.assign(defaultContent, existingContent);
                }
              });
            });

          this.setState({
            industryIndex: tagCompanyIndustryIndex,
            companyInfo: companyInfoMerged,
            companyProfile: companyProfileMerged,
            companyOffice: companyOfficeMerged,
            companyPerson: companyPersonMerged,
            _dbLoaded: true
          });
        });
    } else if (companyKey === 'new') {
      const tagCompanyIndustryIndexRef = firebase.database()
        .ref(`/tagCompanyIndustryIndex`);
      tagCompanyIndustryIndexRef.once('value')
        .then((snapshot) => {
          const data = snapshot.val();
          const tagCompanyIndustryIndex = data ? Object.keys(data)
            .map(key => ({ value: key, label: key })) : [];

          this.setState({
            industryIndex: tagCompanyIndustryIndex,
            companyInfo: new Company(),
            companyProfile: [
              new Block('showcase', 'B', this.getBlockContent('showcase', 'B')),
              new Block('showcase', 'A', this.getBlockContent('showcase', 'A')),
              new Block('content', 'A', this.getBlockContent('content', 'A')),
              new Block('showcase', 'B', this.getBlockContent('showcase', 'B')),
              new Block('content', 'B', this.getBlockContent('content', 'B'))
            ],
            companyOffice: [
              new Block('showcase', 'A', this.getBlockContent('showcase', 'A')),
              new Block('content', 'B', this.getBlockContent('content', 'B')),
              new Block('content', 'A', this.getBlockContent('content', 'A'))
            ],
            companyPerson: {},
            _dbLoaded: true
          });
        });
    }
  }

  // Check changes to the hash to scroll to the ref using CompanyFormNav
  componentDidUpdate() {
    if (this.props.location && this.props.location.hash.replace('#', '') !== this.state._scrolledSection) {
      const hash = this.props.location.hash.replace('#', '');
      const node = ReactDOM.findDOMNode(this.refs[hash]);
      if (node) {
        window.scroll({ left: 0, top: node.offsetTop - 50, behavior: 'instant' });
      }
      this.setState({ _scrolledSection: hash });
    }
  }

  getBlockContent(blockType, layoutType) {
    if (blockType === 'content') {
      switch (layoutType) {
        case 'A':
          return [
            new PhotoCell('photo-small'),
            new PhotoCell('photo-small'),
            new ParagraphCell('paragraph-medium'),
            new ParagraphCell('paragraph-medium'),
            new PhotoCell('photo-small'),
            new PhotoCell('photo-small')
          ];
        case 'B':
          return [
            new PhotoCell('photo-small'),
            new PhotoCell('photo-small'),
            new ParagraphCell('paragraph-medium'),
            new ParagraphCell('paragraph-medium'),
            new PhotoCell('photo-small'),
            new PhotoCell('photo-small')
          ];
        default:
          return [];
      }
    } else if (blockType === 'showcase') {
      switch (layoutType) {
        case 'A':
          return [new VideoCell('video-huge')];
        case 'B':
          return [
            new PhotoCell('photo-hover'),
            new PhotoCell('photo-small'),
            new PhotoCell('photo-small')
          ];
        default:
          return [];
      }
    }
    return [];
  }

  generateCell(cellType) {
    switch (cellType) {
      case 'video-medium':
        return new VideoCell(cellType);
      case 'video-large':
        return new VideoCell(cellType);
      case 'video-huge':
        return new VideoCell(cellType);
      case 'photo-hover':
        return new PhotoCell('photo-large');
      case 'photo-banner':
        return new PhotoCell('photo-large');
      case 'photo-dual':
        return new PhotoCell('photo-small');
      case 'photo-small':
        return new PhotoCell(cellType);
      case 'photo-large':
        return new PhotoCell(cellType);
      case 'photo-carousel':
        return new PhotoCarouselCell(cellType);
      case 'paragraph-small':
        return new ParagraphCell(cellType);
      case 'paragraph-medium':
        return new ParagraphCell(cellType);
      case 'paragraph-huge':
        return new ParagraphCell(cellType);
      case 'info':
        return new AddressCell('address');
      case 'address':
        return new AddressCell(cellType);
      case 'blogpost':
        return new BlogPostCell(cellType);
      case 'none':
        return new Cell('none');
      default:
        return null;
    }
  }

  handleCompanyStatusChange = (option) => {
    let newState = Object.assign(this.state);
    newState.companyInfo.status = option.value;
    this.setState(newState);
  }

  // Change company info, with optional limiter of string length
  handleCompanyInfoChange = (e, lengthLimit) => {
    let newState = Object.assign(this.state);
    const value = lengthLimit ? truncateString(e.target.value, lengthLimit) : e.target.value;
    newState.companyInfo[e.target.name] = value;
    this.setState(newState);
  }

  handleCompanyInfoToggle = (e) => {
    let newState = Object.assign(this.state);
    newState.companyInfo[e.target.name] = newState.companyInfo[e.target.name] ? !newState.companyInfo[e.target.name] : true;
    this.setState(newState);
  }

  handleCompanyIndustryChange = (option) => {
    let newState = Object.assign(this.state);
    newState.companyInfo.industry = option.value;
    this.setState(newState);
  }

  handleCompanySizeChange = (option) => {
    let newState = Object.assign(this.state);
    newState.companyInfo.size = option.value;
    this.setState(newState);
  }

  handleAddEmployee = () => {
    let newState = Object.assign(this.state);
    let { employees } = newState.companyInfo;
    employees.push(new Employee());
    this.setState(newState);
  }

  handleRemoveEmployee = (index) => {
    let newState = Object.assign(this.state);
    let { employees } = newState.companyInfo;
    employees.splice(index, 1);
    this.setState(newState);
  }

  handleEmployeeLock = (index) => {
    let newState = Object.assign(this.state);
    let { employees } = newState.companyInfo;
    const { code } = employees[index];
    if (code) {
      employees[index].codeLocked = true;
      newState.companyPerson[code] = [
        new Block('showcase', 'B', this.getBlockContent('showcase', 'B')),
        new Block('showcase', 'A', this.getBlockContent('showcase', 'A')),
        new Block('content', 'A', this.getBlockContent('content', 'A')),
        new Block('content', 'B', this.getBlockContent('content', 'B')),
        new Block('content', 'B', this.getBlockContent('content', 'B'))
      ];
      this.setState(newState);
    } else {
      toast.error('Error: No employee code');
    }
  }

  handleEmployeeDetailChange = (index, e) => {
    let newState = Object.assign(this.state);
    let targetEmployee = newState.companyInfo.employees[index];
    targetEmployee[e.target.name] = e.target.value;
    this.setState(newState);
  }

  handleEmployeeGenderChange = (index, option) => {
    let newState = Object.assign(this.state);
    let targetEmployee = newState.companyInfo.employees[index];
    targetEmployee.gender = option.value;
    this.setState(newState);
  }

  // Optional personCode 
  handleAddBlock = (category, personCode) => {
    let newState = Object.assign(this.state);
    let targetCategory = personCode ? newState[category][personCode] : newState[category];
    targetCategory.push(new Block('content', 'A'));
    targetCategory[targetCategory.length - 1].content = this.getBlockContent('content', 'A');
    this.setState(newState);
  }

  handleShiftBlockUp = (category, personCode, blockIndex) => {
    let newState = Object.assign(this.state);
    let targetCategory = personCode ? newState[category][personCode] : newState[category];
    // Save the current object temporarily
    const temp = Object.assign({}, targetCategory[blockIndex]);
    targetCategory[blockIndex] = targetCategory[blockIndex - 1];
    targetCategory[blockIndex - 1] = temp;
    this.setState(newState);
  }

  handleShiftBlockDown = (category, personCode, blockIndex) => {
    let newState = Object.assign(this.state);
    let targetCategory = personCode ? newState[category][personCode] : newState[category];
    // Save the current object temporarily
    const temp = Object.assign({}, targetCategory[blockIndex]);
    targetCategory[blockIndex] = targetCategory[blockIndex + 1];
    targetCategory[blockIndex + 1] = temp;
    this.setState(newState);
  }

  handleRemoveBlock = (category, personCode, blockIndex) => {
    let newState = Object.assign(this.state);
    let targetCategory = personCode ? newState[category][personCode] : newState[category];
    targetCategory.splice(blockIndex, 1);
    this.setState(newState);
  }

  handleBlockTypeChange = (category, personCode, blockNumber, option) => {
    let newState = Object.assign(this.state);
    let targetBlock = personCode ? newState[category][personCode][blockNumber] : newState[category][blockNumber];
    targetBlock.type = option.value;
    targetBlock.content = this.getBlockContent(targetBlock.type, targetBlock.layout);
    this.setState(newState);
  }

  handleLayoutTypeChange = (category, personCode, blockNumber, option) => {
    let newState = Object.assign(this.state);
    let targetBlock = personCode ? newState[category][personCode][blockNumber] : newState[category][blockNumber];
    targetBlock.layout = option.value;
    targetBlock.content = this.getBlockContent(targetBlock.type, targetBlock.layout);
    this.setState(newState);
  }

  handleBlockHeaderChange = (category, personCode, blockNumber, e) => {
    let newState = Object.assign(this.state);
    let targetBlock = personCode ? newState[category][personCode][blockNumber] : newState[category][blockNumber];
    targetBlock.header = e.target.value;
    this.setState(newState);
  }

  handleCellTypeChange = (e) => {
    let newState = Object.assign(this.state);
    let targetBlock = e.personCode ? newState[e.category][e.personCode][e.blockNumber] : newState[e.category][e.blockNumber];
    let { content } = targetBlock;
    content[e.cellIndex] = this.generateCell(e.option.value);
    // If the option selected is 'paragraph-medium' or 'video-medium', disable the next cell
    if (e.option.value === 'paragraph-medium' || e.option.value === 'video-medium') {
      content[e.cellIndex + 1] = this.generateCell('none');
    }
    this.setState(newState);
  }

  handleCellContentChange = (e) => {
    let newState = Object.assign(this.state);
    let targetBlock = e.personCode ? newState[e.category][e.personCode][e.blockNumber] : newState[e.category][e.blockNumber];
    let { content } = targetBlock;
    if (e.event.target.name.indexOf('carouselphoto-caption') !== -1) {
      const index = parseInt(e.event.target.name.replace('carouselphoto-caption', ''), 10);
      content[e.cellIndex]['captions'][index] = e.event.target.value;
    } else if (e.event.target.name.indexOf('carouselphoto') !== -1) {
      const index = parseInt(e.event.target.name.replace('carouselphoto', ''), 10);
      content[e.cellIndex]['photos'][index] = e.event.target.value;
    } else {
      content[e.cellIndex][e.event.target.name] = e.event.target.value;
    }
    this.setState(newState);
  }

  handleCellSelectChange = (e) => {
    let newState = Object.assign(this.state);
    let targetBlock = e.personCode ? newState[e.category][e.personCode][e.blockNumber] : newState[e.category][e.blockNumber];
    let { content } = targetBlock;
    content[e.cellIndex] = Object.assign(content[e.cellIndex], e.data);
    this.setState(newState);
  }

  handleAddCarouselPhoto = (e) => {
    let newState = Object.assign(this.state);
    let targetBlock = e.personCode ? newState[e.category][e.personCode][e.blockNumber] : newState[e.category][e.blockNumber];
    let { content } = targetBlock;
    content[e.cellIndex].photos.push('');
    content[e.cellIndex].captions.push('');
    this.setState(newState);
  }

  handleRemoveCarouselPhoto = (e) => {
    let newState = Object.assign(this.state);
    let targetBlock = e.personCode ? newState[e.category][e.personCode][e.blockNumber] : newState[e.category][e.blockNumber];
    let { content } = targetBlock;
    content[e.cellIndex].photos.pop();
    content[e.cellIndex].captions.pop();
    this.setState(newState);
  }

  handleSave = () => {
    const { companyInfo, companyProfile, companyOffice, companyPerson } = this.state;
    const companyKey = companyInfo.code;
    const now = moment()
      .toISOString();
    const datePublished = (!companyInfo.datePublished && companyInfo.status === 'published') ? now : '';

    // Set updated and published dates if applicable
    this.setState({
      ...this.state,
      companyInfo: {
        ...companyInfo,
        datePublished: companyInfo.datePublished ? companyInfo.datePublished : datePublished,
        dateUpdated: now,
        codeLocked: true,
      }
    }, () => {
      this.databaseToast = toast.info('Saving...');
      if (companyKey) {
        this.setState({ _isBusy: true });

        const infoRef = `/company/${companyKey}`;
        const indexRef = `/companyIndex/${companyKey}`;
        const tagIndustryIndexRef = `/tagCompanyIndustryIndex/${companyInfo.industry}`;
        const profileRef = `/companyProfile/${companyKey}`;
        const officeRef = `/companyOffice/${companyKey}`;
        const personRef = `/companyPerson/${companyKey}`;

        let updatePromises = [
          this.props.firebase.update(infoRef, { ...companyInfo, codeLocked: true }),
          this.props.firebase.update(indexRef, {
            status: companyInfo.status,
            datePublished: companyInfo.datePublished ? companyInfo.datePublished : datePublished,
            name: companyInfo.name,
            logo: companyInfo.logo,
            industry: companyInfo.industry,
            size: companyInfo.size,
            previewPrimary: companyInfo.previewPrimary || '',
            previewSecondary: companyInfo.previewSecondary || '',
            featurePhoto: companyInfo.featurePhoto || '',
            subtitle: companyInfo.subtitle,
          }),
          this.props.firebase.set(tagIndustryIndexRef, true),
          this.props.firebase.set(profileRef, companyProfile),
          this.props.firebase.set(officeRef, companyOffice),
          this.props.firebase.set(personRef, companyPerson)
        ];

        Promise.all(updatePromises)
          .then(() => {
            this.setState({ _isBusy: false });
            toast.update(
              this.databaseToast, {
                render: 'Save success!',
                type: toast.TYPE.SUCCESS,
                autoClose: 3000,
                className: 'CompanyForm-toast-transition'
              }
            );
            if (this.props.companyKey === 'new') {
              this.props.dispatch(push(`/companyform?company=${companyInfo.code}`));
            }
          })
          .catch((error) => {
            this.setState({ _isBusy: false });
            toast.update(
              this.databaseToast, {
                render: `Error: ${error.message}`,
                type: toast.TYPE.ERROR,
                autoClose: 3000,
                className: 'CompanyForm-toast-transition'
              }
            );
          });
      }
    });
  }

  handleNavBack = () => {
    this.props.dispatch(push('/companies'));
  }

  _formComplete = () => {
    const { companyInfo } = this.state;
    if (!companyInfo.code || !companyInfo.name || !companyInfo.industry || !companyInfo.size) {
      return false;
    }
    return true;
  }

  _renderEmployeeEditor = () => {
    const { employees } = this.state.companyInfo;

    return (
      employees.map((employee, index) => (
        <div key={`employee-${index}`} className='CompanyForm-employee'>
          <div className='CompanyForm-block-header'>
            <h3>{employee.fullName || 'New Employee'}</h3>
            <button className='ContentBlockEditor-removeButton' type='button' onClick={() => this.handleRemoveEmployee(index)}>
              <i className='fa fa-minus-circle' />
              {' '}Remove Employee
            </button>
          </div>
          <div className='CompanyForm-employee-row'>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='text'
                name='code'
                title='Employee Code'
                subtitle={
                  `A UNIQUE one-word, all-smallcaps slug that will show up on the URL (e.g. https://talenttribe.asia/companies/company/people/{code})
                  Once ready, hit the "Lock" button to lock the employee code in.
                  WARNING: ONCE LOCKED, THE CODE CANNOT BE CHANGED`
                }
                value={employee.code}
                buttonText={employee.codeLocked ? 'LOCKED' : 'LOCK'}
                buttonIcon='fa-lock'
                buttonDisabled={employee.codeLocked}
                errorCode={!employee.codeLocked && employees.filter(person => person.code === employee.code).length > 1 ? 'duplicate-code' : null}
                onChange={(e) => this.handleEmployeeDetailChange(index, e)}
                onButtonClick={() => this.handleEmployeeLock(index)}
              />
            </div>
          </div>
          <div className='CompanyForm-employee-row'>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='text'
                name='fullName'
                title='Full Name'
                subtitle={!employee.codeLocked ? 'Disabled till employee code is locked in' : 'Self-explanatory'}
                value={employee.fullName}
                disabled={!employee.codeLocked}
                onChange={(e) => this.handleEmployeeDetailChange(index, e)}
              />
            </div>
          </div>
          <div className='CompanyForm-employee-row'>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='text'
                name='firstName'
                title='First Name'
                subtitle={!employee.codeLocked ? 'Disabled till employee code is locked in' : 'Self-explanatory'}
                value={employee.firstName}
                disabled={!employee.codeLocked}
                onChange={(e) => this.handleEmployeeDetailChange(index, e)}
              />
            </div>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='text'
                name='lastName'
                title='Last Name'
                subtitle={!employee.codeLocked ? 'Disabled till employee code is locked in' : 'Self-explanatory'}
                value={employee.lastName}
                disabled={!employee.codeLocked}
                onChange={(e) => this.handleEmployeeDetailChange(index, e)}
              />
            </div>
          </div>
          <div className='CompanyForm-employee-row'>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='select'
                name='gender'
                title='Gender'
                subtitle={!employee.codeLocked ? 'Disabled till employee code is locked in' : 'Self-explanatory'}
                value={employee.gender}
                options={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' }
                ]}
                disabled={!employee.codeLocked}
                onChange={(option) => this.handleEmployeeGenderChange(index, option)}
              />
            </div>
          </div>
          <div className='CompanyForm-employee-row'>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='text'
                name='position'
                title='Position'
                subtitle={!employee.codeLocked ? 'Disabled till employee code is locked in' : 'Employee\'s role in company'}
                value={employee.position}
                disabled={!employee.codeLocked}
                onChange={(e) => this.handleEmployeeDetailChange(index, e)}
              />
            </div>
          </div>
          <div className='CompanyForm-employee-row'>
            <div className='CompanyForm-employee-col-1'>
              <FormField
                type='text'
                name='photo'
                title='Employee photo'
                subtitle={!employee.codeLocked ? 'Disabled till employee code is locked in' : 'Portrait photo of employee (shown in company profile page, jobs page etc.)'}
                value={employee.photo}
                disabled={!employee.codeLocked}
                onChange={(e) => this.handleEmployeeDetailChange(index, e)}
              />
            </div>
          </div>
        </div>
      ))
    );
  }

  // Optional parameter personCode when targetting employees
  _renderBlockEditor = (category, personCode) => {
    personCode = personCode || null;
    const section = personCode ? this.state[category][personCode] : this.state[category];
    // Grab the first name (or a psuedo-equivalent) of the person if it exists
    let firstName;
    if (personCode) {
      const { employees } = this.state.companyInfo;
      const employee = employees[employees.findIndex(person => person.code === personCode)];
      if (employee.firstName) {
        // If "First Name" field was filled out, set the section label to that
        firstName = employee.firstName;
      } else if (employee.fullName) {
        // If "Full Name" field was filled out, derive first name from that        
        firstName = employee.fullName.substr(0, employee.fullName.indexOf(' '));
      } else {
        // If no fields were filled out, the section label is the person code
        firstName = personCode;
      }
    }
    return (
      section.map((block, index) => (
        <div key={`block-${index}`} className='CompanyForm-block'>
          <div className='CompanyForm-block-header' ref={(personCode ? personCode : category.replace('company', '').toLowerCase()) + (index + 1)}>
            <h3>{personCode ? `${firstName} - ` : ''}Block {index + 1}</h3>
            <div className='CompanyForm-block-header-buttons'>
              <button className='ContentBlockEditor-positionButton' type='button' onClick={() => this.handleShiftBlockUp(category, personCode, index)} disabled={index === 0}>
                <i className='fa fa-arrow-up' />
              </button>
              <button className='ContentBlockEditor-positionButton' type='button' onClick={() => this.handleShiftBlockDown(category, personCode, index)} disabled={index === section.length - 1}>
                <i className='fa fa-arrow-down' />
              </button>
              <button className='ContentBlockEditor-removeButton' type='button' onClick={() => this.handleRemoveBlock(category, personCode, index)}>
                <i className='fa fa-minus-circle' />
                {' '}Remove Block
              </button>
            </div>
          </div>
          <FormField
            type='select'
            title='Block Type'
            subtitle='Content block type. Refer to the picture for more details.'
            value={block.type}
            options={[
              { value: 'content', label: 'Content' },
              { value: 'showcase', label: 'Showcase' }
            ]}
            onChange={(option) => this.handleBlockTypeChange(category, personCode, index, option)}
          />
          <FormField
            type='select'
            title='Layout Type'
            subtitle='Layout type of the block. Refer to the picture for more details.'
            value={block.layout || ''}
            options={[
              { value: 'A', label: this._renderLayoutTypeText(block.type, 'A') },
              { value: 'B', label: this._renderLayoutTypeText(block.type, 'B') }
            ]}
            onChange={(option) => this.handleLayoutTypeChange(category, personCode, index, option)}
          />
          <ContentBlockEditor
            category={category}
            personCode={personCode}
            blockNumber={index}
            type={block.type}
            layout={block.layout}
            header={block.header}
            content={block.content}
            blogPosts={this.props.blogPosts}
            onChangeBlockHeader={(e) => this.handleBlockHeaderChange(category, personCode, index, e)}
            onChangeCellType={this.handleCellTypeChange}
            onChangeCellContent={this.handleCellContentChange}
            onSelectCellContent={this.handleCellSelectChange}
            onAddCarouselPhoto={this.handleAddCarouselPhoto}
            onRemoveCarouselPhoto={this.handleRemoveCarouselPhoto}
          />
        </div>
      ))
    );
  }

  // Renders the help text for the layout selection
  _renderLayoutTypeText = (blockType, layoutType) => {
    if (blockType === 'content') {
      switch (layoutType) {
        case 'A':
          return 'Layout A - 3 equal columns (1x2)';
        case 'B':
          return 'Layout B - 1 full (1x2) with 2 partial rows (2x1)';
        default:
          return 'Error: Could not retrieve Layout help text';
      }
    } else if (blockType === 'showcase') {
      switch (layoutType) {
        case 'A':
          return 'Layout A - 1 huge box (3x2)';
        case 'B':
          return 'Layout B - 1 large box (2x2) with 1 full column (1x2)';
        default:
          return 'Error: Could not retrieve Layout help text';
      }
    }
    return 'Error: Could not retrieve Layout help text';
  }

  render() {
    const { companyKey, companies } = this.props;
    const { industryIndex, companyInfo, companyProfile, companyOffice, companyPerson } = this.state;

    return (
      <div className='CompanyForm-root'>
        {
          !this.state._dbLoaded || !isLoaded(companies) ?
          <div className='CompanyForm-loadingContainer'>
            <img className='CompanyForm-headerImage' src={logoUrl} alt='' />
            <LoadingSpinner />
          </div> :
          <div className='CompanyForm-page'>
            <div className='CompanyForm-header'>
              <div className='CompanyForm-title'>
                <button onClick={this.handleNavBack}>
                  <i className='fa fa-chevron-left' />
                </button>
                <h2>{companyInfo.name || 'New Company'}</h2>
              </div>
              <button
                disabled={!this.state._dbLoaded || !this._formComplete()}
                className='CompanyForm-submitButton'
                type='button'
                onClick={this.handleSave}
              >
                SAVE {(!this.state._dbLoaded || !this._formComplete()) && '(disabled)'}
              </button>
              <img src={companyInfo.logo || ''} alt='' />
            </div>
            <div className='CompanyForm-formContainer'>
              <CompanyFormNav 
                companyInfo={Object.assign({}, companyInfo)}
                companyProfile={companyProfile.slice()}
                companyOffice={companyOffice.slice()}
                companyPerson={Object.assign({}, companyPerson)}
              />
              <form className='CompanyForm-form'>
                <h2 className='CompanyForm-sectionHeader' ref='publish'>
                  PUBLISH STATUS
                  <p className='CompanyForm-sectionSubheader'>Toggle the button below to publish the entry. DO NOT PUBLISH IF FIELDS HAVE NOT YET BEEN FILLED.</p>
                  <p className='CompanyForm-sectionSubheader'>NOTE: The date and time will be saved and tagged the first time you publish.</p>
                </h2>
                <FormField
                  type='select'
                  name='status'
                  title='Publish Status'
                  subtitle='Set to published in order to show the entry publicly'
                  value={companyInfo.status}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                    { value: 'disabled', label: 'Disabled' }
                  ]}
                  onChange={this.handleCompanyStatusChange}
                />
                <h2 className='CompanyForm-sectionHeader' ref='essential'>
                  Essential Info
                  <p className='CompanyForm-sectionSubheader'>Fill all fields in this section to enable saving</p>
                </h2>
                <FormField
                  type='text'
                  name='code'
                  title='Company Code*'
                  subtitle={
                    `A UNIQUE one-word, all-smallcaps slug that will show up on the URL (e.g. https://talenttribe.asia/companies/{code})
                    NOTE: ONCE SAVED, CANNOT BE CHANGED`
                  }
                  value={companyInfo.code}
                  disabled={companyInfo.codeLocked}
                  errorCode={(companyKey === 'new' && Object.keys(companies).indexOf(companyInfo.code) !== -1) ? 'duplicate-code' : null}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='name'
                  title='Company Name*'
                  subtitle='Self-explanatory'
                  value={companyInfo.name}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='tag'
                  name='industry'
                  title='Company Industry*'
                  subtitle='Tag for industry so that users can filter while browsing (caps-sensitive)'
                  value={companyInfo.industry}
                  options={industryIndex}
                  onChange={this.handleCompanyIndustryChange}
                />
                <FormField
                  type='select'
                  name='size'
                  title='Company Size*'
                  subtitle='Tag for size so that users can filter while browsing'
                  value={companyInfo.size}
                  options={[
                    { value: 'small', label: 'Small' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'large', label: 'Large' }
                  ]}
                  onChange={this.handleCompanySizeChange}
                />
                <br />
                <h2 className='CompanyForm-sectionHeader' ref='address'>Company Info</h2>
                <FormField
                  type='text'
                  name='featurePhoto'
                  title='Company Feature Photo'
                  subtitle='URL of feature photo (if the company is featured on the front page)'
                  value={companyInfo.featurePhoto}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='jobPhoto'
                  title='Company Job Photo'
                  subtitle='URL of job photo (which shows for every job listing belonging to the company)'
                  value={companyInfo.jobPhoto}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='logo'
                  title='Company Logo'
                  subtitle='URL of company logo image'
                  value={companyInfo.logo}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='address'
                  title='Company Address'
                  subtitle='Company office address'
                  value={companyInfo.address}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='gmaps'
                  title='Company Location (Google Maps)'
                  subtitle='A Google Maps link to the company offices'
                  value={companyInfo.gmaps}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='mrt'
                  title='Nearest MRT'
                  subtitle='The nearest MRT stop to the company offices'
                  value={companyInfo.mrt}
                  onChange={this.handleCompanyInfoChange}
                />
                {/* <FormField
                  type='text'
                  name='facebook'
                  title='Facebook Page'
                  subtitle='Company Facebook Page URL'
                  value={companyInfo.facebook}
                  onChange={this.handleCompanyInfoChange}
                /> */}
                {/* <FormField
                  type='text'
                  name='instagram'
                  title='Instagram Page'
                  subtitle='Company Instagram Page URL'
                  value={companyInfo.instagram}
                  onChange={this.handleCompanyInfoChange}
                /> */}
                <FormField
                  type='text'
                  name='linkedin'
                  title='LinkedIn Page'
                  subtitle='Company LinkedIn Page URL'
                  value={companyInfo.linkedin}
                  onChange={this.handleCompanyInfoChange}
                />
                <br />
                <h2 className='CompanyForm-sectionHeader' ref='preview'>Company Preview</h2>
                <FormField
                  type='text'
                  name='subtitle'
                  title='Preview Subtitle'
                  subtitle='A short writeup of the company. Limit to 120 characters.'
                  value={companyInfo.subtitle}
                  onChange={(e) => this.handleCompanyInfoChange(e, 120)}
                />
                <FormField
                  type='text'
                  name='previewPrimary'
                  title='Preview Thumbnail (primary)'
                  subtitle='A photo URL of the company that will show in the listing (i.e. at "https://talenttribe.asia/companies")'
                  value={companyInfo.previewPrimary}
                  onChange={this.handleCompanyInfoChange}
                />
                <FormField
                  type='text'
                  name='previewSecondary'
                  title='Preview Thumbnail (secondary)'
                  subtitle='A photo URL of the company that will show in the listing (when hovered over with the mouse)'
                  value={companyInfo.previewSecondary}
                  onChange={this.handleCompanyInfoChange}
                />
                <br />
                <div className='CompanyForm-employees'>
                  <div className='CompanyForm-sectionHeaderContainer'>
                    <h2 className='CompanyForm-sectionHeader' ref='employees'>Company Employees</h2>
                    <div className='CompanyForm-checkboxContainer'>
                      <p>Enabled:</p>
                      <FormField
                        type='checkbox'
                        name='employeesEnabled'
                        value={companyInfo.employeesEnabled}
                        onToggle={this.handleCompanyInfoToggle}
                      />
                    </div>
                  </div>
                  { companyInfo.employeesEnabled && companyInfo.employees && this._renderEmployeeEditor() }
                  {
                    companyInfo.employeesEnabled &&
                    <div className='CompanyForm-block-buttonContainer'>
                      <button className='CompanyForm-block-addButton' type='button' onClick={() => this.handleAddEmployee()}>
                        <i className='fa fa-plus-circle' />
                        {' '}Add Employee
                      </button>
                    </div>
                  }
                </div>
                <div className='CompanyForm-profile'>
                  <h2 className='CompanyForm-sectionHeader' ref='profile'>Company Profile</h2>
                  { this._renderBlockEditor('companyProfile') }
                  <div className='CompanyForm-block-buttonContainer'>
                    <button className='CompanyForm-block-addButton' type='button' onClick={() => this.handleAddBlock('companyProfile')}>
                      <i className='fa fa-plus-circle' />
                      {' '}Add Block
                    </button>
                  </div>
                </div>
                <div className='CompanyForm-office'>
                  <div className='CompanyForm-sectionHeaderContainer'>
                    <h2 className='CompanyForm-sectionHeader' ref='office'>Company Office</h2>
                    <div className='CompanyForm-checkboxContainer'>
                      <p>Enabled:</p>
                      <FormField
                        type='checkbox'
                        name='officeEnabled'
                        value={companyInfo.officeEnabled}
                        onToggle={this.handleCompanyInfoToggle}
                      />
                    </div>
                  </div>
                  { companyInfo.officeEnabled && this._renderBlockEditor('companyOffice') }
                  {
                    companyInfo.officeEnabled &&
                    <div className='CompanyForm-block-buttonContainer'>
                      <button className='CompanyForm-block-addButton' type='button' onClick={() => this.handleAddBlock('companyOffice')}>
                        <i className='fa fa-plus-circle' />
                        {' '}Add Block
                      </button>
                    </div>
                  }
                </div>
                {
                  companyInfo.employeesEnabled &&
                  <div className='CompanyForm-employees'>
                    <h2 className='CompanyForm-sectionHeader' ref='people'>
                      Company People
                      <p className='CompanyForm-sectionSubheader'>
                        Employees will start appearing here when their codes have been typed in and finalized.
                        <br />
                        You have a total of {companyInfo.employees.length} employees, of which
                        a total of {companyInfo.employees.filter(person => !person.code).length} employees do not have codes.
                      </p>
                    </h2>
                    { 
                      companyInfo.employees.map(person => (
                        person.code && companyPerson[person.code] &&
                        <div key={`person-${person.code}`} className='CompanyForm-person' ref={person.code}>
                          { this._renderBlockEditor('companyPerson', person.code) }
                          <div className='CompanyForm-block-buttonContainer'>
                            <button className='CompanyForm-block-addButton' type='button' onClick={() => this.handleAddBlock('companyPerson', person.code)}>
                              <i className='fa fa-plus-circle' />
                              {' '}Add Block
                            </button>
                          </div>
                        </div>
                      ))
                    }
                    
                  </div>
                }
              </form>
            </div>
          </div>
        }
      </div>
    );
  }
}

const connectedCompanyForm = connect(
  (state) => ({
    companies: state.firebase.data.company,
    location: state.router.location
  })
)(CompanyForm);

const wrappedCompanyForm = firebaseConnect([
  { path: '/company' }
])(connectedCompanyForm);

export default wrappedCompanyForm;



// WEBPACK FOOTER //
// ./src/components/companyform/CompanyForm/CompanyForm.js